import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children?: React.ReactNode;
}

/**
 * 인증이 필요한 라우트를 보호하는 컴포넌트
 * 인증되지 않은 사용자는 로그인 페이지로 리디렉션됩니다.
 */
export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // ⭐ 디버그 로그 추가
  console.log('[ProtectedRoute] 체크:', {
    path: location.pathname,
    isAuthenticated,
    isLoading,
    hasUser: !!user,
    userInfo: user ? {
      memberNo: user.memberNo,
      memberType: user.memberType,
      apiKey: user.apiKey?.substring(0, 10) + '...'
    } : null
  });

  // 인증 상태 로딩 중에는 로딩 화면 표시
  if (isLoading) {
    console.log('[ProtectedRoute] 로딩 중...');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin text-primary-600 mb-4">
            <i className="ri-loader-4-line text-4xl"></i>
          </div>
          <p className="text-gray-700">로딩 중...</p>
        </div>
      </div>
    );
  }

  // 인증되지 않은 상태면 로그인 페이지로 리디렉션
  if (!isAuthenticated) {
    console.log('[ProtectedRoute] ❌ 인증 실패 - /login으로 리다이렉트');
    console.log('[ProtectedRoute] localStorage user:', localStorage.getItem('user'));
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  console.log('[ProtectedRoute] ✅ 인증 성공 - 페이지 렌더링');
  return <>{children ? children : <Outlet />}</>;
};

/**
 * 인증된 사용자는 특정 페이지(로그인, 회원가입 등)에 접근하면
 * 홈페이지로 리디렉션하는 컴포넌트
 */
export const PublicRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading } = useAuth();

  console.log('[PublicRoute] 체크:', { isAuthenticated, isLoading });

  if (isLoading) {
    console.log('[PublicRoute] 로딩 중...');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin text-primary-600 mb-4">
            <i className="ri-loader-4-line text-4xl"></i>
          </div>
          <p className="text-gray-700">로딩 중...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    console.log('[PublicRoute] 이미 인증됨 - / 로 리다이렉트');
    return <Navigate to="/" replace />;
  }

  console.log('[PublicRoute] 미인증 - 페이지 렌더링');
  return <>{children ? children : <Outlet />}</>;
};